import tenancy from '@/plugins/tenancy'
import Company from './Company'
import Model from './interface/Model'
import Api from './Api'
import PaginateOptions from './interface/PaginateOptions'
import FeatureAccess from './FeatureAccess'
import DefaultPermissions from './DefaultPermissions'
import SelectOption from './interface/SelectOption'

export default class Tenant extends Model {
  protected api_settings = {
    save_mode: 'post',
    paths: {
      singular: 'tenant' as string,
      plural: 'tenants' as string,
    },
  }

  public id: any = null

  public domains: any = []

  public connect: any = false

  public domain: any = null

  public status: any = null

  public app_url: any = null

  // model - string (events_cpm, impressions_cpm,none)
  // rate - double
  // access - json

  public model: string = 'none'

  public rate: number = 0

  public url_aliases: any = []

  public is_hok: boolean = false

  /**
   * The name of the first user created after tenant creation
   */
  public username: any = null

  public get tenantPermissions() {
    const { tenantPermissions } = tenancy({})
    return tenantPermissions(DefaultPermissions)
  }

  public access: FeatureAccess[] = JSON.parse(JSON.stringify(this.tenantPermissions))

  public getAccessIndex(model: string, action: string) {
    return this.access.findIndex(item => item.model === model && item.action === action)
  }

  public can(model: string, action: string = 'any', company_level: boolean = false) {
    let permission
    if (action == 'any') {
      permission = this.company.access.find(p => p.model === model && p.access === true)
    } else {
      permission = this.company.access.find(p => p.model === model && p.action === action)
    }

    if (!permission || company_level) {
      return permission ? permission.access : false
    }

    if (action == 'any') {
      permission = this.access.find(p => p.model === model && p.access === true)
    } else {
      permission = this.access.find(p => p.model === model && p.action === action)
    }

    if (!permission) {
      return false
    }
    return permission.access
  }

  private _actions: string[] = []

  public get actions() {
    if (this._actions.length == 0) {
      this._actions = this.access
        .map(item => item.action)
        .filter((value, index, self) => self.indexOf(value) === index)
    }

    return this._actions
  }

  public hasAction(model: string, action: string) {
    return this.access.some(item => item.model === model && item.action === action)
  }

  public database_settings: any = {
    tenancy_db_name: '',
    tenancy_db_username: '',
    tenancy_db_password: '',
  }

  public company_id: any = null

  public company: Company = new Company()

  public get url_aliases_options() {
    return this.url_aliases.map((alias: any) => new SelectOption(alias, alias))
  }

  public get apiData() {
    let base: any = {
      id: this.id,
      domain: this.domain,
      connect: this.connect,
      database_settings: {},
      is_hok: this.is_hok,
      url_aliases: this.url_aliases,
    }
    if (this.app_url && this.app_url.length) {
      base.app_url = this.app_url
    }
    if (this.company_id && this.company_id.length) {
      base.company_id = this.company_id
    }
    if (
      this.database_settings.tenancy_db_username
      && this.database_settings.tenancy_db_username.length
    ) {
      base.database_settings.tenancy_db_username = this.database_settings.tenancy_db_username
    }
    if (
      this.database_settings.tenancy_db_password
      && this.database_settings.tenancy_db_password.length
    ) {
      base.database_settings.tenancy_db_password = this.database_settings.tenancy_db_password
    }
    if (this.database_settings.tenancy_db_name && this.database_settings.tenancy_db_name?.length) {
      base.database_settings.tenancy_db_name = this.database_settings.tenancy_db_name
    }

    if (this.username && this.username.length) {
      base.username = this.username
    }

    let access_options: any = {}

    this.access?.forEach(item => {
      if (item.options.length > 0) {
        access_options[item.model] = item.options
      }
    })

    base.access = this.access
      .filter(item => item.access)
      .map(item => `${item.model}:${item.action}`)

    base.access_options = access_options

    return base
  }

  public toObject(source: any) {
    let instance = this.clone()

    for (const model in source.access) {
      for (const idx in source.access[model]) {
        const action = source.access[model][idx]
        const index = instance.getAccessIndex(model, action)
        if (index >= 0) instance.access[index].access = true
      }
    }

    if (source.access_options) {
      for (const model in source.access_options) {
        const index = instance.getAccessIndex(model, 'options')
        if (index >= 0) instance.access[index].options = source.access_options[model]
      }
      delete source.access_options
    }

    instance.id = source.id

    instance.domain = source.domains.length ? source.domains[0].domain : null

    if (source.company) {
      instance.company = Company.toObject(source.company)
    }

    if (source.company !== undefined) delete source.company

    if (source.tenancy_db_name) {
      instance.database_settings.tenancy_db_name = source.tenancy_db_name
      delete source.tenancy_db_name
    }

    if (source.access !== undefined) delete source.access

    Object.assign(instance, source)

    return instance
  }

  public static toObjectList(source: any) {
    return source.map((item: any) => Tenant.toObject(item))
  }

  public async databaseList() {
    const api = new Api()
    return api
      .get('database-list')
      .then(response => response.data)
      .catch(error => error)
  }

  public async deleteOnDemand(to_delete: any) {
    const api = new Api()
    return api
      .post(`tenants/delete/${this.id}`, to_delete)
      .then(response => response.data)
      .catch(error => error)
  }

  public static async paginate(options: PaginateOptions) {
    const api = new Api(false)
    return api
      .get('tenants/paginate', options)
      .then(response => {
        const data = Tenant.toObjectList(response.data.result.tenants)

        return {
          records: response.data.result.records,
          shared_data: response.data.result.shared_data,
          data,
        }
      })
      .catch(e => ({
        records: 0,
        shared_data: 0,
        data: [],
      }))
  }

  public static async find(id: any) {
    const api = new Api()
    return api
      .get(`tenant/${id}`)
      .then(response => Tenant.toObject(response.data.result.tenant))
      .catch(error => error)
  }

  public async save(id: any = null) {
    const api = new Api()
    return api
      .post(this.api_settings.paths.plural + (id ? `/${id}` : ''), this.apiData)
      .then(response => response.data)
      .catch(error => error)
  }

  public static async loadTenants() {
    const api = new Api()
    return api
      .get('shareable-tenants')
      .then(response => response.data.result)
      .catch(error => error)
  }

  public static async shareModel(payload: any) {
    const api = new Api()
    return api
      .post('share-data', payload)
      .then(response => response.data.result)
      .catch(error => error)
  }

  public static models: any = [
    {
      name: 'Events CPM',
      value: 'events_cpm',
    },
    {
      name: 'Impressions CPM',
      value: 'impressions_cpm',
    },
    {
      name: 'None',
      value: 'none',
    },
  ]
}
